import { useState, useEffect, useRef } from "react";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { useFormDnD } from "../../hooks/useFormDnD";
import { FormSection, AvailableFields, AddSection } from "./components";
import { Section, Field } from "./types";
import {
  getKeyByValue,
  getTypeLabel,
  isCustomField,
  isIntegrationSection,
  mapEntities,
} from "./utils";
import { FieldPreview } from "./components/FieldPreview";
import { useFormLayoutLabels } from "@/contexts/translation.context";
import { formatTranslation } from "@/utils";
import { MuxDragHandle2, MuxChevronRight } from "@/Icons";

type FormLayoutProps = {
  entityAvailableFields: {
    systemFields: Record<string, Field>;
    customFields: Record<string, Field>;
  };
  entitySections: Section[];
  entity: string;
  shouldReload: boolean;
  handleDataChange: (
    data: Record<string, unknown>,
    newSectionsState: Section[],
  ) => void;
};

function FormLayout({
  entityAvailableFields,
  entitySections,
  entity,
  shouldReload,
  handleDataChange,
}: FormLayoutProps) {
  const labels = useFormLayoutLabels();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(true);
  const [sections, setSections] = useState<Section[]>(entitySections);
  const [availableFieldsState, setAvailableFieldsState] = useState<{
    systemFields: Record<string, Field>;
    customFields: Record<string, Field>;
  }>(entityAvailableFields);
  const [deletedSectionIds, setDeletedSectionIds] = useState<string[]>([]);
  const prevSectionsRef = useRef(sections);
  const findAvailableFields = (key: string) => {
    const fields = isCustomField(key)
      ? availableFieldsState.customFields
      : availableFieldsState.systemFields;
    return Object.values(fields).find((f: Field) => f.id == key);
  };

  const dnd = useFormDnD({
    sections,
    availableFieldsState,
    setSections,
    setAvailableFieldsState,
    findAvailableFields,
  });

  useEffect(() => {
    setSections(entitySections);
  }, [entitySections]);

  useEffect(() => {
    setAvailableFieldsState(entityAvailableFields);
    setDeletedSectionIds([]);
  }, [entityAvailableFields]);

  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      prevSectionsRef.current = sections;
      return;
    }

    handleDataChange(prepareDataSentToServer(), sections);
  }, [sections]);

  const prepareDataSentToServer = () => {
    const payload = {
      entity_name: getKeyByValue(mapEntities, entity),
      sections: sections
        .filter((section) => {
          // Keep section if id is a string (new section) or if numeric id is positive
          return (
            section.id !== "DESCRIPTION" &&
            section.id !== "TEST_VERSIONING" &&
            section.id !== "VALUE_SCORE" &&
            section.id !== "STEP_PARAMETERS"
          );
        })
        .map((section) => ({
          id: section.id.startsWith("section-") ? "" : section.id,
          label: section?.label,
          row_position: section.row,
          column_position: section.column,
          fields: section.fields.map((field) => field.id),
        })),
      deleted_sections: deletedSectionIds,
    };

    return payload;
  };

  // Restore helper: can add section
  const canAddSection =
    entity !== "run" &&
    sections.filter((section) => !section.isSystem).length < 5;

  // Restore handleRemoveField
  const handleRemoveField = async (sectionId: string, field: Field) => {
    if (field.mandatory) {
      const confirmRemoval = window.confirm(labels.mandatoryFieldRemoval);
      if (!confirmRemoval) {
        return;
      }
    }

    const sectionsAfterRemoval = sections.map((s) => {
      if (s.id == sectionId) {
        return {
          ...s,
          fields: s.fields.filter((f) => f.id !== field.id),
        };
      }
      return s;
    });
    // Remove field from section
    setSections(sectionsAfterRemoval);

    // Add field back to available fields
    setAvailableFieldsState((prevFields) => {
      // Create new fields object with the field added back
      const newFields = field.id.startsWith("__")
        ? {
            ...prevFields,
            customFields: {
              ...prevFields.customFields,
              [field.id]: field,
            },
          }
        : {
            ...prevFields,
            systemFields: {
              ...prevFields.systemFields,
              [field.id]: field,
            },
          };

      // Sort fields alphabetically by label
      const sortFields = (fields: Record<string, Field>) => {
        const entries = Object.entries(fields);
        entries.sort(([, a], [, b]) =>
          (a.label || "").localeCompare(b.label || ""),
        );
        return Object.fromEntries(entries);
      };

      // Return sorted fields
      return {
        systemFields: sortFields(newFields.systemFields),
        customFields: sortFields(newFields.customFields),
      };
    });
  };

  // Restore handleRemoveSection
  const handleRemoveSection = (sectionId: string) => {
    const sectionToRemove = sections.find(
      (section) => section.id.toString() === sectionId,
    );
    if (!sectionToRemove?.id) return;

    if (sectionToRemove) {
      sectionToRemove.fields.forEach((field) => {
        handleRemoveField(sectionToRemove.id, field);
      });
    }
    // Remove the section
    setSections((sections) =>
      sections.filter((section) => section.id.toString() !== sectionId),
    );

    if (
      !!sectionToRemove.id &&
      !sectionToRemove.id.toString().startsWith("section_")
    ) {
      setDeletedSectionIds((prev) => [...prev, sectionToRemove.id.toString()]);
    }
  };

  // Restore handleSectionTitleUpdate
  const handleSectionTitleUpdate = (sectionId: string, newTitle: string) => {
    setSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId ? { ...section, label: newTitle } : section,
      ),
    );
  };

  const handleAddSection = (column: number) => {
    // Get the next order number for this column
    const columnSections = sections.filter((s) => Number(s.column) === column);
    const maxOrder = Math.max(...columnSections.map((s) => s.row || 0), -1);

    const newSection: Section = {
      id: `section-${sections.length + 1}`,
      label: "",
      fields: [],
      row: maxOrder + 1,
      column,
      isSystem: false,
      disabled: false,
    };

    // Add the new section and sort all sections
    setSections((currentSections) => {
      const newSections = [...currentSections, newSection];

      // Sort sections by column and order
      return newSections.sort((a, b) => {
        // First sort by column
        if (a.column !== b.column) return a.column - b.column;
        // Then by order within column
        return (a.row || 0) - (b.row || 0);
      });
    });
  };

  if (shouldReload || !sections) return <div>Loading....</div>;

  const leftColumnSections = sections.filter((section) => section.column === 0);
  const rightColumnSections = sections.filter(
    (section) => section.column === 1,
  );
  const notOnPageCount =
    Object.keys(availableFieldsState.systemFields || {}).length +
    Object.keys(availableFieldsState.customFields || {}).length;

  return (
    <DndContext
      sensors={dnd.sensors}
      onDragStart={dnd.handleDragStart}
      onDragEnd={dnd.handleDragEnd}
      onDragCancel={() => dnd.setDraggedField(null)}
    >
      <>
        <div className="form-content">
          <div
            className={`form-main ${sections.length == 0 ? "form-main-empty" : ""}`}
          >
            <div className="form-column">
              <div className="form-sections">
                {leftColumnSections
                  .sort((a, b) => a.row - b.row)
                  .map((section) =>
                    (() => {
                      if (section.id === "DESCRIPTION") {
                        return (
                          <div key={section.id} className="form-section">
                            <div className="section-header-2">
                              <div className="section-title">
                                <button
                                  className={`icon-button section-toggle transition-ease ${isDescriptionExpanded ? "expanded" : ""}`}
                                  onClick={() =>
                                    setIsDescriptionExpanded(
                                      !isDescriptionExpanded,
                                    )
                                  }
                                >
                                  <MuxChevronRight />
                                </button>
                                <span className="section-title-input">
                                  {labels.descriptionLabel}
                                </span>
                              </div>
                              <div className="section-controls">
                                <span className="section-fields-count">1</span>
                              </div>
                            </div>
                            {isDescriptionExpanded && (
                              <div className="section-content">
                                <div className="fields-container-2">
                                  <div className="form-field">
                                    <div className="field-item field-item-static">
                                      <div className="drag-area drag-area-disabled">
                                        <MuxDragHandle2 />
                                      </div>
                                      <div className="field-info">
                                        <span className="field-name">
                                          {labels.descriptionLabel}
                                        </span>
                                        <span className="field-type">
                                          {getTypeLabel("memo")}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      return (
                        <FormSection
                          key={section.id}
                          id={section.id}
                          // ToDo: fix typescript error
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          name={section.name}
                          disabled={section.disabled}
                          title={section.label}
                          fields={section.fields || []}
                          onRemove={() =>
                            handleRemoveSection(String(section.id))
                          }
                          onRemoveField={(field) =>
                            handleRemoveField(String(section.id), field)
                          }
                          onUpdateTitle={(sectionId, newTitle) =>
                            handleSectionTitleUpdate(
                              String(sectionId),
                              newTitle,
                            )
                          }
                          isSystem={section.isSystem}
                          className={
                            isIntegrationSection(section)
                              ? "integration-section"
                              : ""
                          }
                        />
                      );
                    })(),
                  )}
              </div>
              <AddSection
                onAddSection={() => handleAddSection(0)}
                disabled={!canAddSection}
              />
            </div>
            <div className="form-column">
              <div className="form-sections">
                {rightColumnSections
                  .sort((a, b) => a.row - b.row)
                  .map((section) =>
                    (() => {
                      switch (section.id) {
                        case "TEST_VERSIONING":
                        case "STEP_PARAMETERS":
                          return (
                            <div
                              key={section.id}
                              className="description-field form-control"
                            >
                              {section.label}
                            </div>
                          );
                        default:
                          return (
                            <FormSection
                              key={section.id}
                              id={section.id}
                              // ToDo: fix typescript error
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              name={section.name}
                              title={section.label}
                              fields={section.fields || []}
                              onRemove={() =>
                                handleRemoveSection(String(section.id))
                              }
                              onRemoveField={(field) =>
                                handleRemoveField(String(section.id), field)
                              }
                              onUpdateTitle={(sectionId, newTitle) =>
                                handleSectionTitleUpdate(
                                  String(sectionId),
                                  newTitle,
                                )
                              }
                              isSystem={section.isSystem}
                              disabled={section.disabled}
                              info={
                                section.id === "VALUE_SCORE"
                                  ? labels.valueScoreTooltip
                                  : undefined
                              }
                            />
                          );
                      }
                    })(),
                  )}
              </div>
              <AddSection
                onAddSection={() => handleAddSection(1)}
                disabled={!canAddSection}
              />
            </div>
          </div>

          <div
            className={`available-fields-panel ${sections.length == 0 ? "form-sidebar-disabled" : ""}`}
          >
            <div className="available-fields-panel-header">
              <div className="title">{labels.availableFields}</div>
              <div className="fields-count">
                {formatTranslation(
                  "sections.labels.fields-not-on-page",
                  `${notOnPageCount} not on the page`,
                  { count: notOnPageCount },
                )}
              </div>
            </div>
            <AvailableFields
              fields={availableFieldsState}
              disabled={sections.length == 0}
            />
          </div>
        </div>
      </>
      <DragOverlay>
        <FieldPreview field={dnd?.draggedField} />
      </DragOverlay>
    </DndContext>
  );
}

export default FormLayout;
